/** QuickRouter — Équipotentielles : résolution des broches, pastilles par net, connectivité
 *  réelle du cuivre (union-find) et chevelu (ratsnest) des liaisons restant à router. */
import { worldPads, padShape, segShape, discShape, shapeDist, shapeBox, boxesNear, pointInPoly, dist } from './geom.js';
import { copperLayers } from './layers.js';
import { netRule } from './netclasses.js';

/** Toutes les pastilles de la carte : [{ ...pad monde, fp }] */
export function allPads(project, lookup) {
  const out = [];
  const cu = copperLayers(project.board);
  for (const c of project.components) {
    const fp = lookup(c.footprint);
    for (const p of worldPads(c, fp, cu)) out.push(p);
  }
  return out;
}

/**
 * « U1.3 », « U1.GND », « J2.A0 » → clés canoniques « REF.NUM » (toutes les pastilles portant
 * ce numéro ou ce nom). Lève une erreur explicite si le repère ou la broche n'existe pas.
 */
export function resolvePin(project, lookup, spec) {
  const s = String(spec).trim();
  const dot = s.lastIndexOf('.');
  if (dot <= 0) throw new Error(`broche « ${s} » : format attendu REPÈRE.BROCHE (ex. U1.3 ou U1.GND)`);
  const ref = s.slice(0, dot), pin = s.slice(dot + 1);
  const comp = project.components.find((c) => c.ref === ref);
  if (!comp) throw new Error(`broche « ${s} » : aucun composant de repère ${ref}`);
  const fp = lookup(comp.footprint);
  if (!fp) throw new Error(`broche « ${s} » : empreinte ${comp.footprint} introuvable`);
  const byNum = fp.pads.filter((p) => p.num === pin);
  if (byNum.length) return [`${ref}.${pin}`];
  const byName = [...new Set(fp.pads.filter((p) => p.name && p.name.toLowerCase() === pin.toLowerCase()).map((p) => p.num))];
  if (byName.length) return byName.map((n) => `${ref}.${n}`);
  const names = [...new Set(fp.pads.map((p) => (p.name ? `${p.num}=${p.name}` : p.num)))].slice(0, 60).join(', ');
  throw new Error(`broche « ${s} » : ${ref} (${fp.id}) n'a pas de broche « ${pin} » — broches : ${names}`);
}

/** Map clé de pastille « REF.NUM » → nom du net. */
export function padNetMap(project) {
  const m = new Map();
  for (const n of project.nets) for (const k of n.pins) m.set(k, n.name);
  return m;
}

/** Largeur de piste d'un net (largeur imposée > classe power > règle signal). */
export function netWidth(project, netName, layer = 'F') {
  const n = project.nets.find((x) => x.name === netName);
  if (n?.width) return n.width;
  // Impédance contrôlée (classe ou valeur du net) : largeur calculée sur l'empilage.
  const rule = netRule(project, n, layer);
  if (rule?.width) return rule.width;
  return n?.kind === 'power' ? project.rules.powerWidth : project.rules.trackWidth;
}

class DSU {
  constructor(n) { this.p = Array.from({ length: n }, (_, i) => i); }
  find(i) { while (this.p[i] !== i) { this.p[i] = this.p[this.p[i]]; i = this.p[i]; } return i; }
  union(a, b) { const x = this.find(a), y = this.find(b); if (x !== y) this.p[x] = y; }
}

const TOUCH = 0.001;

/** Éléments de cuivre d'un net : pastilles, segments de piste, vias, zones. */
function copperItems(project, pads, netName) {
  const items = [];
  for (const p of pads) {
    if (!p.layers.length) continue;
    items.push({ type: 'pad', key: p.key, pos: p.pos, layers: p.layers, shape: padShape(p) });
  }
  for (const t of project.tracks) {
    if (t.net !== netName) continue;
    for (let i = 1; i < t.pts.length; i++) {
      items.push({ type: 'seg', track: t.id, pos: t.pts[i - 1], layers: [t.layer], shape: segShape(t.pts[i - 1], t.pts[i], t.width) });
    }
  }
  for (const v of project.vias) {
    if (v.net !== netName) continue;
    items.push({ type: 'via', id: v.id, pos: v.pos, layers: copperLayers(project.board), shape: discShape(v.pos, v.diameter) });
  }
  for (const z of project.zones) {
    if (z.net !== netName) continue;
    items.push({ type: 'zone', id: z.id, layers: [z.layer], outline: z.outline ?? project.board.outline });
  }
  for (const it of items) if (it.shape) it.box = shapeBox(it.shape);
  return items;
}

function touches(a, b) {
  if (!a.layers.some((l) => b.layers.includes(l))) return false;
  if (a.type === 'zone' && b.type === 'zone') return false;
  if (a.type === 'zone' || b.type === 'zone') {
    const z = a.type === 'zone' ? a : b, o = a.type === 'zone' ? b : a;
    // Approximation : une pastille, un via ou une piste dont le centre est dans la zone y est raccordé.
    return o.type !== 'seg' && pointInPoly(o.pos, z.outline);
  }
  if (!boxesNear(a.box, b.box, TOUCH)) return false;
  return shapeDist(a.shape, b.shape) <= TOUCH;
}

/**
 * Connectivité d'un net : groupes de cuivre reliés. Rend { pads, clusters, rats }
 * où rats = liaisons manquantes [{ a, b, from, to }] (arbre couvrant minimal entre groupes).
 */
export function netConnectivity(project, lookup, netName, padsAll = null) {
  const net = project.nets.find((n) => n.name === netName);
  if (!net) return { pads: 0, clusters: 0, rats: [] };
  const keys = new Set(net.pins);
  const pads = (padsAll ?? allPads(project, lookup)).filter((p) => keys.has(p.key));
  const items = copperItems(project, pads, netName);
  const dsu = new DSU(items.length);
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (items[i].type === 'pad' && items[j].type === 'pad' && items[i].key === items[j].key) { dsu.union(i, j); continue; }
      if (touches(items[i], items[j])) dsu.union(i, j);
    }
  }
  // Groupes contenant au moins une pastille (le cuivre orphelin ne compte pas).
  const groups = new Map();
  items.forEach((it, i) => {
    const root = dsu.find(i);
    if (!groups.has(root)) groups.set(root, { pads: [], pts: [] });
    const g = groups.get(root);
    if (it.type === 'pad') { g.pads.push(it); g.pts.push(it.pos); }
    else if (it.pos) g.pts.push(it.pos);
  });
  const clusters = [...groups.values()].filter((g) => g.pads.length);
  return { pads: pads.length, clusters: clusters.length, rats: spanningRats(clusters) };
}

/** Arbre couvrant minimal (Prim) entre groupes : distance = paire de points la plus proche. */
function spanningRats(clusters) {
  if (clusters.length < 2) return [];
  const inTree = [0];
  const rest = new Set(clusters.map((_, i) => i).slice(1));
  const rats = [];
  while (rest.size) {
    let best = null;
    for (const i of inTree) {
      for (const j of rest) {
        for (const p of clusters[i].pts) {
          for (const q of clusters[j].pads) {
            const d = dist(p, q.pos);
            if (!best || d < best.d) best = { d, j, a: p, b: q.pos, to: q.key };
          }
        }
      }
    }
    const from = clusters[inTree[0]].pads[0]?.key;
    rats.push({ a: best.a, b: best.b, from, to: best.to, length: best.d });
    inTree.push(best.j);
    rest.delete(best.j);
  }
  return rats;
}

/** État de routage de toute la carte. */
export function routingStatus(project, lookup) {
  const pads = allPads(project, lookup);
  const nets = [];
  let unrouted = 0;
  for (const n of project.nets) {
    if (n.pins.length < 2) { nets.push({ name: n.name, pads: n.pins.length, clusters: n.pins.length, rats: [], done: true }); continue; }
    const c = netConnectivity(project, lookup, n.name, pads);
    unrouted += c.rats.length;
    nets.push({ name: n.name, pads: c.pads, clusters: c.clusters, rats: c.rats, done: c.rats.length === 0 });
  }
  return { nets, unrouted, routed: nets.filter((n) => n.done).length, total: nets.length };
}
